import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, Typography, TextField } from '@mui/material';

/**
 * 2단계 라인별 정답 입력 컴포넌트
 * content에서 ___를 포함한 라인을 찾아 각 라인별로 정답 입력 필드 표시
 * ___가 제거되면 해당 라인의 정답이 lineAnswers에서 빠지고, 폼의 word(저장 시 keyword)도 동일 규칙으로 갱신됨
 */

/** TrainingAdd / TrainingDetail onSubmit과 동일: 빈 값 제외 → '/' 연결 → 공백을 ','로 */
function lineAnswersToWord(lineAnswers) {
    if (!Array.isArray(lineAnswers)) return '';
    const filtered = lineAnswers.filter(Boolean);
    if (filtered.length === 0) return '';
    return filtered.join('/').replace(/\s+/g, ',');
}

const Step2LineAnswersInput = ({ setValue, watch, selectedFileIndex, setStep2SelectedLine }) => {
    const content = watch(`step2Files.${selectedFileIndex}.content`) || '';
    const lineAnswers = watch(`step2Files.${selectedFileIndex}.lineAnswers`) || [];

    const linesWithBlank = useMemo(() => {
        return content.split('\n')
            .map((line, i) => (line.includes('___') ? { lineNumber: i + 1, line } : null))
            .filter(Boolean);
    }, [content]);

    const prevLinesWithBlankRef = useRef([]);

    useEffect(() => {
        const prev = prevLinesWithBlankRef.current;
        const prevLineNumbers = prev.map((p) => p.lineNumber);
        const currLineNumbers = linesWithBlank.map((l) => l.lineNumber);

        const hasChanged = prevLineNumbers.length !== currLineNumbers.length
            || prevLineNumbers.some((n, i) => n !== currLineNumbers[i]);

        if (prev.length > 0 && hasChanged) {
            const newAnswers = linesWithBlank.map(({ lineNumber }) => {
                const oldIdx = prevLineNumbers.indexOf(lineNumber);
                return oldIdx >= 0 ? (lineAnswers[oldIdx] ?? '') : '';
            });
            setValue(`step2Files.${selectedFileIndex}.lineAnswers`, newAnswers, { shouldDirty: true });
            setValue(`step2Files.${selectedFileIndex}.word`, lineAnswersToWord(newAnswers), { shouldDirty: true });
        }
        prevLinesWithBlankRef.current = linesWithBlank;
    }, [content, lineAnswers, linesWithBlank, selectedFileIndex, setValue]);

    const handleAnswerChange = useCallback((index, value) => {
        const current = watch(`step2Files.${selectedFileIndex}.lineAnswers`) || [];
        const next = [...current];
        while (next.length <= index) next.push('');
        next[index] = value;
        setValue(`step2Files.${selectedFileIndex}.lineAnswers`, next, { shouldDirty: true });
        setValue(`step2Files.${selectedFileIndex}.word`, lineAnswersToWord(next), { shouldDirty: true });
    }, [selectedFileIndex, setValue, watch]);

    if (linesWithBlank.length === 0) {
        return (
            <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    라인별 정답
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    에디터에서 빈 칸(___)을 포함한 라인을 추가하면, 해당 라인별로 정답을 입력할 수 있습니다.
                    같은 라인에 여러 개의 빈 칸이 있다면, 공백으로 구분하여 정답을 입력해주세요.
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
                라인별 정답
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {linesWithBlank.map(({ lineNumber, line }, idx) => (
                    <Box key={lineNumber} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ minWidth: 52, color: 'text.secondary' }}>
                            라인 {lineNumber}
                        </Typography>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="정답 입력"
                            value={(lineAnswers[idx] ?? '')}
                            onFocus={() => setStep2SelectedLine(lineNumber)}
                            onChange={(e) => handleAnswerChange(idx, e.target.value)}
                            sx={{ flex: 1 }}
                        />
                    </Box>
                ))}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                각 빈 칸(___)이 있는 라인에 해당하는 정답을 입력하세요.
            </Typography>
        </Box>
    );
};

export default Step2LineAnswersInput;
